import { Flex, Spacer, Text, VStack } from '@chakra-ui/react'
import React from 'react'
import IconCloseXBold from '~/assets/svgs/icon-close-x-bold.svg?react'
import IconEditPencil from '~/assets/svgs/icon-edit-pencil.svg?react'
import IconPlus from '~/assets/svgs/icon-plus.svg?react'
import { useModalState } from '~/atoms/ModalState'
import { CallMode, Ringtone, useSettingsState } from '~/atoms/SettingsState'
import { useLastOutboundPhoneNumber } from '~/atoms/WebPhoneInteractionState'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { TextButton } from '~/components/shared/TextButton'
import { Size } from '~/types/ThemeTypes'
import { SpeedDial } from '~/types/WebphoneTypes'

import { usePhoneSettingsStyles } from './componentStyles/PhoneSettings.styles'
import { PhoneSettingToggleDependentSection } from './PhoneSettingItems'
import { SettingToggle } from './SettingItems'
import {
  DefaultSettingsWrapper,
  DropdownWrapper,
  SubheadingSection,
} from './Shared'

interface PhoneSettingsProps {
  pbxId: number | undefined
}

const MAX_SPEED_DIALS = 50

export const PhoneSettings = ({ pbxId }: PhoneSettingsProps) => {
  const {
    settings: {
      webPhone: {
        ringtone: defaultRingtone,
        hasCallWaiting,
        allowClickToCallIncoming,
        enableCallerIdSelection,
      },
      clickToCall: { clickToCallMode, shouldAutoAnswer },
      pbx: { parkingLots, extensions },
      speedDial,
    },
    canUseWebPhone,
    removeParkingLot,
    removeTrackedExtension,
    setRingtone,
    setHasCallWaiting,
    setAllowClickToCallIncoming,
    setClickToCallMode,
    setShouldAutoAnswer,
    setEnableCallerIdSelection,
    removeSpeedDial,
  } = useSettingsState(pbxId)
  const [, setLastUsedOutboundPhone] = useLastOutboundPhoneNumber()

  const styles = usePhoneSettingsStyles()

  const {
    parkingModal: [, setParkingModalState],
    extensionModal: [, setExtensionModalState],
    speedDialModal: [, setSpeedDialModalState],
  } = useModalState()

  const CallModeMap = new Map<CallMode, string>([
    [CallMode.CLICK_TO_CALL, 'Send to Desk Phone (Yealink)'],
  ])
  if (canUseWebPhone) {
    CallModeMap.set(CallMode.WEBPHONE, 'Use Web Phone')
  }
  const handleSilenceRingtoneToggle = () => {
    if (defaultRingtone === Ringtone.DEFAULT) {
      setRingtone(Ringtone.SILENT)
    } else {
      setRingtone(Ringtone.DEFAULT)
    }
  }
  const handleCallWaitingToggle = () => {
    setHasCallWaiting(!hasCallWaiting)
  }
  const handleAllowClickToCallIncomingToggle = () => {
    setAllowClickToCallIncoming(!allowClickToCallIncoming)
  }

  const handleChangeCallModeSelection = (callMode: CallMode) => {
    setClickToCallMode(callMode)
  }
  const handleAutoAnswerToggle = () => {
    setShouldAutoAnswer(!shouldAutoAnswer)
  }

  const handleRemoveParkingLot = (id: number) => {
    removeParkingLot(id)
  }
  const handleAddParkingLot = () => {
    setParkingModalState({ open: true })
  }

  const handleRemoveExtension = (id: number) => {
    removeTrackedExtension(id)
  }
  const handleAddExtension = () => {
    setExtensionModalState({ open: true })
  }

  const handleEnableCallerIdSelection = () => {
    if (enableCallerIdSelection) {
      setLastUsedOutboundPhone(null)
    }
    setEnableCallerIdSelection(!enableCallerIdSelection)
  }

  const handleAddSpeedDial = () => {
    setSpeedDialModalState({ open: true })
  }
  const handleEditSpeedDial = (speedDial: SpeedDial) => {
    setSpeedDialModalState({ open: true, speedDial })
  }

  const clickToCallDropdownOptions = Array.from(
    CallModeMap,
    ([key, content]) => ({
      value: key,
      content: content,
    }),
  )

  const isRingtoneActive = defaultRingtone !== Ringtone.SILENT

  const shouldShowClickToCallDropdown = clickToCallDropdownOptions.length > 1
  const shouldShowAutoAnswer = clickToCallMode === CallMode.CLICK_TO_CALL

  return (
    <DefaultSettingsWrapper>
      <PhoneSettingToggleDependentSection
        isChecked={canUseWebPhone}
        toggleOnContent={
          <VStack sx={styles.vStack1}>
            <SubheadingSection>
              <Text id={'incomingCalls'} sx={styles.text1}>
                Incoming Calls
              </Text>
              <SettingToggle
                label="Show Inbound C2C Calls"
                isChecked={allowClickToCallIncoming}
                onClick={handleAllowClickToCallIncomingToggle}
              />
              <SettingToggle
                label="Play Ringtone"
                isChecked={isRingtoneActive}
                onClick={handleSilenceRingtoneToggle}
              />
              <SettingToggle
                label="Respond with Busy to Second Call"
                isChecked={hasCallWaiting ?? true}
                onClick={handleCallWaitingToggle}
              />
            </SubheadingSection>
            <SubheadingSection>
              <Text id={'outgoingCalls'} sx={styles.text1}>
                Outgoing Calls
              </Text>

              {shouldShowClickToCallDropdown && (
                <DropdownWrapper>
                  <Text sx={styles.text2}>
                    Select default phone for outbound calls
                  </Text>
                  <DropdownSelect
                    items={clickToCallDropdownOptions}
                    selectedValue={clickToCallMode}
                    onSelect={handleChangeCallModeSelection}
                  />
                </DropdownWrapper>
              )}
              {shouldShowAutoAnswer && (
                <SettingToggle
                  label="Auto Answer"
                  isChecked={shouldAutoAnswer}
                  onClick={handleAutoAnswerToggle}
                />
              )}
              <Text id={'callerId'} textStyle="Body2_Bold">
                Caller ID
              </Text>
              <SettingToggle
                label="Enable Caller ID selection"
                subLabel="Allows you to choose the outgoing phone number when making calls."
                isChecked={enableCallerIdSelection}
                onClick={handleEnableCallerIdSelection}
              />
            </SubheadingSection>
            <SubheadingSection>
              <Text id={'presence'} sx={styles.text1}>
                Presence
              </Text>
              <Text sx={styles.text2}>
                The following sections will be displayed on your web phone
                giving you quick access to your most dialed extensions and
                numbers.
              </Text>
              <Flex
                width={'100%'}
                borderRadius={'0.25rem'}
                display={'flex'}
                flexDir={'column'}
                marginTop={'0.5rem'}
                alignItems={'flex-start'}
                gap={'0.25rem'}
              >
                <Text sx={styles.text3}>Parking Spots Displayed (Limit 5)</Text>
                {parkingLots?.map((lot) => {
                  return (
                    <Flex key={lot.id} sx={styles.parkingContainer}>
                      <Text sx={styles.text2}>{lot.label}</Text>
                      {lot.label !== lot.defaultLabel && (
                        <Text sx={styles.text4}>({lot.defaultLabel})</Text>
                      )}
                      <Spacer />
                      <QuickActionButton
                        variant={QuickActionButtonVariants.Default_100}
                        size={Size.sm}
                        actionIcon={IconCloseXBold}
                        tooltipText="Remove Parking Spot"
                        onClick={() => handleRemoveParkingLot(lot.id)}
                        iconProps={{ boxSize: '0.75rem' }}
                      />
                    </Flex>
                  )
                })}
                {(parkingLots?.length ?? 0) < 5 && (
                  <TextButton
                    leftIcon={IconPlus}
                    onLinkClick={handleAddParkingLot}
                    textButtonProps={{ sx: { padding: '.5rem' } }}
                    leftIconProps={{
                      sx: {
                        boxSize: '12px',
                        position: 'relative',
                        bottom: '.5px',
                        stroke: 'inherit',
                        '& path': { strokeWidth: '5px' },
                      },
                    }}
                  >
                    Add Parking Spot
                  </TextButton>
                )}
              </Flex>

              <Flex
                id={'extensions'}
                width={'100%'}
                borderRadius={'0.25rem'}
                display={'flex'}
                flexDir={'column'}
                marginTop={'0.5rem'}
                alignItems={'flex-start'}
                gap={'0.25rem'}
              >
                <Text sx={styles.text3}>
                  Line Extensions Displayed (Limit 10)
                </Text>
                {extensions?.map((ext) => {
                  return (
                    <Flex key={ext.id} sx={styles.extensionsContainer}>
                      <Text sx={styles.text2}>{ext.name}</Text>
                      <Spacer />
                      <QuickActionButton
                        variant={QuickActionButtonVariants.Default_100}
                        size={Size.sm}
                        actionIcon={IconCloseXBold}
                        tooltipText="Remove Extension"
                        onClick={() => handleRemoveExtension(ext.id)}
                        iconProps={{ boxSize: '0.75rem' }}
                      />
                    </Flex>
                  )
                })}
                {(extensions?.length ?? 0) < 10 && (
                  <TextButton
                    leftIcon={IconPlus}
                    onLinkClick={handleAddExtension}
                    textButtonProps={{ sx: { padding: '.5rem' } }}
                    leftIconProps={{
                      sx: {
                        boxSize: '12px',
                        position: 'relative',
                        bottom: '.5px',
                        stroke: 'inherit',
                        '& path': { strokeWidth: '5px' },
                      },
                    }}
                  >
                    Add Extension
                  </TextButton>
                )}
              </Flex>

              <Flex
                id={'speedDial'}
                width={'100%'}
                borderRadius={'0.25rem'}
                display={'flex'}
                flexDir={'column'}
                marginTop={'0.5rem'}
                alignItems={'flex-start'}
                gap={'0.25rem'}
              >
                <Text sx={styles.text3}>
                  Speed Dial (Limit {MAX_SPEED_DIALS})
                </Text>

                {Array.from(speedDial).map(([key, value]) => (
                  <Flex key={key} sx={styles.speedDialContainer}>
                    <Flex
                      display={'flex'}
                      flexDir={'column'}
                      paddingY={'0.75rem'}
                    >
                      <Text sx={styles.text2}>{value.label}</Text>
                      <Text sx={styles.text4}>{value.number}</Text>
                    </Flex>
                    <Spacer />

                    <QuickActionButton
                      variant={QuickActionButtonVariants.Default_100}
                      size={Size.sm}
                      actionIcon={IconEditPencil}
                      tooltipText="Edit Speed Dial"
                      onClick={() => handleEditSpeedDial(value)}
                      iconProps={{ boxSize: '0.75rem' }}
                    />
                    <QuickActionButton
                      variant={QuickActionButtonVariants.Default_100}
                      size={Size.sm}
                      actionIcon={IconCloseXBold}
                      tooltipText="Remove Speed Dial"
                      onClick={() => removeSpeedDial(Number(key))}
                      iconProps={{ boxSize: '0.75rem' }}
                    />
                  </Flex>
                ))}
                {Array.from(speedDial).length <= MAX_SPEED_DIALS && (
                  <TextButton
                    leftIcon={IconPlus}
                    textButtonProps={{ sx: { padding: '.5rem' } }}
                    onLinkClick={handleAddSpeedDial}
                    leftIconProps={{
                      sx: {
                        boxSize: '12px',
                        position: 'relative',
                        bottom: '.5px',
                        stroke: 'inherit',
                        '& path': { strokeWidth: '5px' },
                      },
                    }}
                  >
                    Add Speed Dial
                  </TextButton>
                )}
              </Flex>
            </SubheadingSection>
          </VStack>
        }
        toggleOffContent={
          <VStack sx={styles.vStack2}>
            <SubheadingSection>
              <Text sx={styles.text1}>Click-to-Call</Text>
              <SettingToggle
                label="Auto Answer"
                isChecked={shouldAutoAnswer}
                onClick={handleAutoAnswerToggle}
              />
            </SubheadingSection>
          </VStack>
        }
      />
    </DefaultSettingsWrapper>
  )
}
